/**
 * @file ClientStatusDropdown.js
 * @description A dropdown component for selecting a client status.
 */

import React, { useState, useEffect } from 'react';
import { fetchClientStatus } from '../../../services/systemService';
import { Dropdown } from '../../helpers/Dropdown';
import LoadingWrapper from '../../ui/loaders/LoadingWrapper';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

/**
 * A dropdown component for selecting client status.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline.
 * @param {string} props.selectedClientStatus - The currently selected client status.
 * @param {Function} props.onClientStatusChange - The callback function to handle client status change.
 * @param {string} [props.label="Client Status"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {string} [props.defaultLabel="Select Client Status"] - The default label for the dropdown.
 * @returns {JSX.Element} The rendered ClientStatusDropdown component.
 */
const ClientStatusDropdown = ({
    className,
    inlineLabel = true,
    selectedClientStatus,
    onClientStatusChange,
    label = 'Client Status',
    isRequired = false,
    defaultLabel = 'Select Client Status',
}) => {
    const [clientStatuses, setClientStatuses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getClientStatuses = async () => {
            setIsLoading(true);
            try {
                const data = await fetchClientStatus();
                setClientStatuses(data);
            } catch (error) {
                console.error('Could not fetch client statuses:', error);
                Toast.error(getText('msg_4fb46f9e7eed'));
            } finally {
                setIsLoading(false);
            }
        };

        getClientStatuses();
    }, []);

    const getDefaultLabel = () => {
        const matchingClientStatus = clientStatuses.find(
            clientStatus => clientStatus.id === selectedClientStatus,
        );
        return matchingClientStatus ? matchingClientStatus.name : defaultLabel;
    };

    const clientStatusOptions = clientStatuses
        .map(clientStatus => ({
            value: clientStatus.id,
            label: clientStatus.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return (
        <div
            className={`form-group mb-3 ${
                inlineLabel ? 'inline-input' : ''
            } ${className}`}
        >
            <label htmlFor="client-status-dropdown">
                {label}
                {isRequired && <span className="text-danger"> *</span>}
            </label>
            <LoadingWrapper isLoading={isLoading}>
                <Dropdown
                    id="client-status-dropdown"
                    defaultValue={getDefaultLabel()}
                    options={clientStatusOptions}
                    value={selectedClientStatus}
                    onChange={onClientStatusChange}
                    disabled={clientStatuses.length === 0 || isLoading}
                />
            </LoadingWrapper>
        </div>
    );
};

export default ClientStatusDropdown;
